<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed, onBeforeMount, ref } from 'vue';

  import type { Task, User } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
  } from '@ui/components/command';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { toast } from '@ui/components/sonner';

  import { stuut } from '@/api';
  import TasksAssignee from '@/components/tasks/TasksAssignee.vue';
  import { UserAvatar } from '@/components/users';
  import { useCoveringUsers } from '@/composables/useCoveringUsers';
  import { useProfile, useTaskStore, useUsers } from '@/store';
  import { users as userUtils } from '@/utils';

  interface Props {
    disabled?: boolean;
    ghostButtonVariant?: boolean;
    hideArrowButton?: boolean;
    showAvatarOnly?: boolean;
    size?: 'xs' | 'sm' | 'md';
    task: Task;
  }

  const props = defineProps<Props>();
  const profileStore = useProfile();
  const userStore = useUsers();
  const taskStore = useTaskStore();
  const { coveredUserIds } = useCoveringUsers();

  const { users } = storeToRefs(userStore);
  const isLoadingUsers = ref(true);
  const isUpdating = ref(false);
  const open = ref(false);

  const canAssignTasks = computed(() => {
    return profileStore.profile?.is_org_admin;
  });

  const isAssigneeCovered = computed(
    () => !!props.task?.assignee?.id && coveredUserIds.value.has(props.task.assignee.id),
  );

  const userGroups = computed(() => {
    return userUtils.getUserGroups(users.value);
  });

  onBeforeMount(() => {
    if (!users.value?.length) {
      userStore
        .fetchUsers()
        .catch((error) => {
          toast.error(error.clientMessage);
        })
        .finally(() => {
          isLoadingUsers.value = false;
        });
    } else {
      isLoadingUsers.value = false;
    }
  });

  const assignTasks = (user: User) => {
    if (isUpdating.value) return;

    isUpdating.value = true;

    return stuut.tasks
      .updateAssignee(props.task, user?.id)
      .then((result) => toast.success(`Successfully assigned to ${result?.assignee?.name}.`))
      .then(() => {
        taskStore.updateAllTasks(props.task);
      })
      .catch((err) => toast.error(`Failed to assign with error: ${err.clientMessage}.`))
      .finally(() => {
        isUpdating.value = false;
      });
  };

  const removeAssignee = () => {
    if (isUpdating.value) return;

    isUpdating.value = true;

    return stuut.tasks
      .removeAssignee(props.task)
      .then(() => toast.success('Successfully removed assignee.'))
      .then(() => taskStore.updateAllTasks(props.task))
      .catch((err) => toast.error(`Failed to remove assignee with error: ${err.clientMessage}.`))
      .finally(() => {
        isUpdating.value = false;
      });
  };
</script>

<!-- TODO: refactor this to use the generic AssigneeSelect.vue the way PartnerAssigneeSelect does -->
<template v-if="task?.id">
  <TasksAssignee
    v-if="!canAssignTasks"
    v-bind="$attrs"
    :show-avatar-only="showAvatarOnly"
    :assignee="task?.assignee"
    :is-ooo-covering="isAssigneeCovered"
    :size="size"
  />

  <Popover v-else v-model:open="open">
    <PopoverTrigger as-child>
      <Button
        :class="[
          {
            'p-0 rounded-full': showAvatarOnly,
            'size-8': showAvatarOnly && size === 'md',
            'size-6': showAvatarOnly && size === 'sm',
            'size-5': showAvatarOnly && size === 'xs',
            'p-0': ghostButtonVariant,
          },
          showAvatarOnly && isAssigneeCovered && 'overflow-visible! justify-start!',
        ]"
        v-bind="$attrs"
        :variant="showAvatarOnly || ghostButtonVariant ? 'transparent' : 'outline'"
        role="combobox"
        size="sm"
        :aria-expanded="open"
        :disabled="isUpdating || isLoadingUsers || props.disabled"
      >
        <TasksAssignee
          :show-avatar-only="showAvatarOnly"
          :assignee="task?.assignee"
          :is-ooo-covering="isAssigneeCovered"
          :size="size"
        />

        <div v-if="!showAvatarOnly && !hideArrowButton" class="ml-2 text-xs">
          <i v-if="isUpdating" class="fa far fa-spinner-third animate-spin" />
          <i v-else class="fa far fa-chevron-down" />
        </div>
      </Button>
    </PopoverTrigger>

    <PopoverContent class="w-[250px] p-0" align="end">
      <Command
        :filter-function="
          (list, term) => {
            term = term.toLowerCase();
            return (list as Record<string, any>[]).filter((i) =>
              i.name?.toLowerCase()?.includes(term),
            );
          }
        "
      >
        <CommandList>
          <div class="relative">
            <CommandInput placeholder="Search user..." />
          </div>
          <CommandEmpty>No users found.</CommandEmpty>
          <div class="h-[200px] overflow-y-auto">
            <CommandGroup v-for="group in userGroups" :key="group.label" :heading="group.label">
              <CommandItem
                v-for="user in group.users"
                :key="user.id"
                :value="user"
                class="flex justify-between text-xs"
                @select="
                  () => {
                    assignTasks(user);
                    open = false;
                  }
                "
              >
                <div class="flex items-center space-x-2">
                  <UserAvatar :user="user" size="sm" />
                  <p>{{ user.name }}</p>
                </div>
              </CommandItem>
            </CommandGroup>

            <CommandSeparator v-if="task?.assignee" />

            <CommandGroup v-if="task?.assignee">
              <CommandItem
                value="Remove assignee"
                class="flex justify-between text-xs"
                @select="
                  () => {
                    removeAssignee();
                    open = false;
                  }
                "
              >
                <div class="flex items-center space-x-2">
                  <div class="inline-flex items-center justify-center w-6 h-6">
                    <i class="fa far fa-close text-destructive"></i>
                  </div>

                  <p>Remove assignee</p>
                </div>
              </CommandItem>
            </CommandGroup>
          </div>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>

<style scoped></style>
